import { Inbox } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // <-- NEU
import { cn } from '../../../../lib/utils.js'; // Pfad ggf. anpassen
import {
  ClarificationTab,
  CopyDetectionTab,
  CurvingTab,
  ExportTab,
  GradeDistributionTab,
  LanguageUseTab,
  KpiCard,
  StudentsOverviewTab,
  TaskInsightsTab,
} from './components/summary-tabs.js';
import { useSummaryState } from './hooks/use-summary-state.js';
import type { CorrectionSummaryProps } from './utils.js';
import { STUDENT_VISIBLE_SUMMARY_TAB_KEYS, SUMMARY_TABS, roundToOne } from './utils.js';

export function CorrectionSummary(props: CorrectionSummaryProps) {
  const { t } = useTranslation(); // <-- NEU
  const store = useSummaryState(props);
  const { state, computed, actions, refs } = store;
  const isStudent = computed.role === 'student';
  const visibleTabs = isStudent
    ? SUMMARY_TABS.filter(tab => STUDENT_VISIBLE_SUMMARY_TAB_KEYS.includes(tab.key))
    : SUMMARY_TABS;

  if (computed.submissions.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
          <Inbox className="h-7 w-7 text-slate-400 dark:text-slate-500" />
        </div>
        <p className="font-medium text-slate-500 dark:text-slate-400">{t('Canvas.summary.noSubmissionsFound')}</p>
      </div>
    );
  }

  return (
    <div
      ref={refs.rootRef}
      className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/70"
    >
      <div className="sticky top-0 z-30 border-b border-slate-200 dark:border-slate-700 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">
        <div className="px-4 pt-4 pb-3">
          <div className={cn('grid gap-3', isStudent ? (computed.isKpiSingleColumn ? 'grid-cols-1' : 'grid-cols-2') : computed.kpiGridColumnsClass)}>
            {!isStudent && (
              <KpiCard
                label={t('Canvas.summary.student')}
                value={String(computed.studentSummaries.length)}
                compact={computed.isKpiCompact}
              />
            )}
            <KpiCard
              label={t('Canvas.summary.grade')} // "Notenschnitt"
              value={computed.averageGrade != null ? computed.averageGrade.toFixed(1) : '—'}
              compact={computed.isKpiCompact}
              className={cn(computed.gradeCardClasses.card, computed.gradeCardClasses.label)}
            />
            <KpiCard
              label={t(isStudent ? 'Canvas.summary.pointsReached' : 'Canvas.summary.avgPointsReached')}
              value={computed.averageTotalPoints.toFixed(1)}
              detail={`/ ${roundToOne(computed.totalPossiblePoints)} ${t('Canvas.summary.pointsAbbr')} (${computed.averagePercentage.toFixed(1)}%)`}
              compact={computed.isKpiCompact}
              className={cn(computed.pointsCardClasses.card, computed.pointsCardClasses.label)}
            />
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-3">
          <div className="inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-1">
            {visibleTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => actions.setActiveTab(tab.key)}
                className={cn(
                  'whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors',
                  state.activeTab === tab.key
                    ? 'bg-brand-500 text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700',
                )}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {state.activeTab === 'students' && <StudentsOverviewTab store={store} />}
        {state.activeTab === 'clarification' && <ClarificationTab store={store} />}
        {state.activeTab === 'insights' && <TaskInsightsTab store={store} />}
        {state.activeTab === 'grades' && <GradeDistributionTab store={store} />}
        {state.activeTab === 'export' && <ExportTab store={store} />}
        {state.activeTab === 'curving' && <CurvingTab store={store} />}
        {state.activeTab === 'languageUse' && <LanguageUseTab store={store} />}
        {state.activeTab === 'copyCheck' && <CopyDetectionTab store={store} />}
      </div>

      <div className="pointer-events-none h-4 bg-gradient-to-t from-slate-50 dark:from-slate-900 to-transparent" />
    </div>
  );
}
